'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useApp } from '@/lib/store'
import { Wind, Phone, Lock, User, ArrowLeft, Eye, EyeOff, AlertCircle, Building2, CheckCircle2 } from 'lucide-react'
import { FileUpload } from '@/components/ui/file-upload'

function LoginContent() {
  const router = useRouter()
  const { login, register } = useApp()
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [registerSuccess, setRegisterSuccess] = useState(false)
  const [formData, setFormData] = useState({
    account: '', // 登录用：用户名或手机号
    phone: '',
    password: '',
    username: '',
    confirmPassword: '',
    companyFullName: '',
    businessLicense: '',
    companyContactPerson: '',
    companyContactPhone: ''
  })

  // 验证手机号格式
  const validatePhone = (phone: string): string | null => {
    if (!phone) return '请输入手机号'
    if (!/^\d+$/.test(phone)) return '手机号必须为纯数字'
    if (phone.length !== 11) return '请输入正确的11位手机号'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (isLogin) {
        const success = await login(formData.account, formData.password)
        if (success) {
          router.push('/admin')
        } else {
          setError('用户名/手机号或密码错误')
        }
      } else {
        // 注册验证
        const phoneError = validatePhone(formData.phone)
        if (phoneError) {
          setError(phoneError)
          setLoading(false)
          return
        }
        if (formData.password !== formData.confirmPassword) {
          setError('两次输入的密码不一致')
          setLoading(false)
          return
        }
        if (formData.password.length < 6) {
          setError('密码长度至少6位')
          setLoading(false)
          return
        }
        if (!formData.companyFullName.trim()) {
          setError('请输入公司全称')
          setLoading(false)
          return
        }
        if (!formData.businessLicense) {
          setError('请上传营业执照')
          setLoading(false)
          return
        }
        if (!formData.companyContactPerson.trim()) {
          setError('请输入联系人')
          setLoading(false)
          return
        }
        if (!formData.companyContactPhone.trim()) {
          setError('请输入联系电话')
          setLoading(false)
          return
        }
        
        // 调用注册API
        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: formData.phone,
            password: formData.password,
            username: formData.username,
            companyFullName: formData.companyFullName,
            businessLicense: formData.businessLicense,
            companyContactPerson: formData.companyContactPerson,
            companyContactPhone: formData.companyContactPhone
          })
        })
        const data = await res.json()
        
        if (data.success) {
          // 注册成功，提示等待审核
          setRegisterSuccess(true)
        } else {
          setError(data.message || '注册失败')
        }
      }
    } catch (err) {
      setError('操作失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* 背景装饰 */}
      <div className="fixed inset-0 sky-gradient" />
      <div className="fixed top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
      <div className="fixed bottom-20 left-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />

      <div className="relative z-10 w-full max-w-md">
        {/* 返回首页 */}
        <Link href="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          返回首页
        </Link>

        <Card className="animate-scale-in">
          <CardHeader className="text-center">
            <div className="w-16 h-16 wind-gradient rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Wind className="w-8 h-8 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">安维士询价平台</CardTitle>
            <CardDescription>
              {isLogin ? '登录您的账户以继续' : '注册新账户开始使用'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {registerSuccess ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">注册成功，等待审核</h3>
                  <p className="text-sm text-muted-foreground">
                    您的账户已提交审核，请等待管理员审核通过后方可登录使用。
                  </p>
                </div>
                <Button 
                  variant="wind" 
                  className="w-full" 
                  size="lg"
                  onClick={() => {
                    setRegisterSuccess(false)
                    setIsLogin(true)
                    setError('')
                  }}
                >
                  返回登录
                </Button>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">用户名</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="请输入用户名"
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      className="pl-10"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              {isLogin ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">用户名/手机号</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="请输入用户名或手机号"
                      value={formData.account}
                      onChange={(e) => setFormData(prev => ({ ...prev, account: e.target.value }))}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">手机号 *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        placeholder="请输入11位手机号（纯数字）"
                        value={formData.phone}
                        onChange={(e) => {
                          // 只允许输入数字
                          const value = e.target.value.replace(/\D/g, '')
                          setFormData(prev => ({ ...prev, phone: value }))
                        }}
                        className="pl-10"
                        maxLength={11}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">密码</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请输入密码"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">确认密码</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="请再次输入密码"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="pl-10"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              {!isLogin && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-muted-foreground mb-3">公司信息</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">公司全称 *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="请输入公司全称"
                        value={formData.companyFullName}
                        onChange={(e) => setFormData(prev => ({ ...prev, companyFullName: e.target.value }))}
                        className="pl-10"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">营业执照 *</label>
                    <FileUpload
                      value={formData.businessLicense}
                      onUpload={(fileUrl) => setFormData(prev => ({ ...prev, businessLicense: fileUrl }))}
                      accept=".pdf,.jpg,.jpeg,.png"
                      maxSize={10}
                      forRegistration={true}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">联系人 *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="请输入联系人姓名"
                        value={formData.companyContactPerson}
                        onChange={(e) => setFormData(prev => ({ ...prev, companyContactPerson: e.target.value }))}
                        className="pl-10"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">联系电话 *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        placeholder="请输入联系电话"
                        value={formData.companyContactPhone}
                        onChange={(e) => setFormData(prev => ({ ...prev, companyContactPhone: e.target.value }))}
                        className="pl-10"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button type="submit" variant="wind" className="w-full" size="lg" disabled={loading}>
                {loading ? '处理中...' : (isLogin ? '登录' : '注册并提交审核')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {isLogin ? '还没有账户？' : '已有账户？'}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin)
                    setError('')
                  }}
                  className="text-primary hover:underline ml-1"
                >
                  {isLogin ? '立即注册' : '立即登录'}
                </button>
              </p>
            </div>
              </>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return <LoginContent />
}
